import { useState, useEffect, useMemo, useCallback } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { FirebaseError } from 'firebase/app';
import { db } from '../../lib/firebase';
import { useAuthState } from '../../lib/partnerAuth';
import { markPartnerPayoutsPaid } from '../../lib/adminPayouts';
import { useSEO } from '../../hooks/useSEO';
import { AdminFilters } from '../../components/partners/AdminFilters';
import { MarkPaidModal } from '../../components/partners/MarkPaidModal';
import { countryCodeToFlag, countryCodeToName } from '../../lib/countryUtils';
import {
  DEFAULT_ADMIN_FILTERS,
  isPayoutEligible,
  MIN_PAYOUT_USD,
  computeAdminStats,
  exportAdminTableCsv,
  type AdminFilterState,
  type AdminPartnerRow,
  type AdminPartnerTableRow,
  type AdminPayoutRow,
  type AdminCampaignOption,
  type ReferralClickRow,
  type ReferralInstallRow,
  type PurchaseRow,
} from '../../lib/partnerStatsFilters';
import styles from './AdminAnalytics.module.css';

export function AdminAnalytics() {
  const { user, isAdmin, loading: authLoading } = useAuthState();
  const [partners, setPartners] = useState<AdminPartnerRow[]>([]);
  const [referrals, setReferrals] = useState<ReferralInstallRow[]>([]);
  const [clicks, setClicks] = useState<ReferralClickRow[]>([]);
  const [purchases, setPurchases] = useState<PurchaseRow[]>([]);
  const [payouts, setPayouts] = useState<AdminPayoutRow[]>([]);
  const [campaigns, setCampaigns] = useState<AdminCampaignOption[]>([]);
  const [filters, setFilters] = useState<AdminFilterState>(DEFAULT_ADMIN_FILTERS);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [markPaidPartner, setMarkPaidPartner] = useState<AdminPartnerTableRow | null>(null);
  const [markPaidLoading, setMarkPaidLoading] = useState(false);
  const [markPaidError, setMarkPaidError] = useState<string | null>(null);

  useSEO({
    title: 'Admin Analytics — LuvLust Partners',
    description: 'Admin analytics dashboard.',
  });

  const loadData = useCallback(async () => {
      setLoadError(null);
      try {
        const [partnerSnap, clickSnap, campaignSnap] = await Promise.all([
          getDocs(collection(db, 'partners')),
          getDocs(collection(db, 'referral_clicks')),
          getDocs(collection(db, 'partner_campaigns')),
        ]);

        const partnerRows: AdminPartnerRow[] = partnerSnap.docs.map((d) => {
          const data = d.data();
          return {
            id: d.id,
            displayName: data.displayName || 'Unknown',
            referralCode: data.referralCode || '',
            email: data.email || '',
            totalEarnings: data.totalEarnings || 0,
            totalPaid: data.totalPaid || 0,
            status: data.status || 'unknown',
          };
        });

        const subcollectionSnaps = await Promise.all(
          partnerSnap.docs.map(async (partnerDoc) => {
            const [referralSnap, payoutSnap, purchaseSnap] = await Promise.all([
              getDocs(collection(db, `partners/${partnerDoc.id}/referrals`)),
              getDocs(collection(db, `partners/${partnerDoc.id}/payouts`)),
              getDocs(collection(db, `partners/${partnerDoc.id}/purchases`)),
            ]);
            return {
              partnerId: partnerDoc.id,
              referralSnap,
              payoutSnap,
              purchaseSnap,
            };
          }),
        );

        const referralRows: ReferralInstallRow[] = [];
        const payoutRows: AdminPayoutRow[] = [];
        const purchaseRows: PurchaseRow[] = [];

        for (const { partnerId, referralSnap, payoutSnap, purchaseSnap } of subcollectionSnaps) {
          for (const d of referralSnap.docs) {
            const data = d.data();
            referralRows.push({
              partnerId,
              platform: data.platform as string | undefined,
              installedAt: data.installedAt,
              method: data.method as string | undefined,
              deviceId: data.deviceId as string | undefined,
              campaignId: data.campaignId as string | undefined,
              country: data.country as string | undefined,
              earningsStatus: data.earningsStatus as string | undefined,
              earningsUSD: data.earningsUSD as number | undefined,
            });
          }
          for (const d of payoutSnap.docs) {
            const data = d.data();
            payoutRows.push({
              partnerId,
              month: data.month || d.id,
              totalInstalls: data.totalInstalls,
              totalEarnings: data.totalEarnings,
              totalCommission: data.totalCommission,
              paidAmount: data.paidAmount ?? 0,
              status: data.status ?? 'pending',
            });
          }
          for (const d of purchaseSnap.docs) {
            const data = d.data();
            purchaseRows.push({
              partnerId,
              platform: data.platform as string | undefined,
              purchasedAt: data.purchasedAt,
              deviceId: data.deviceId as string | undefined,
              campaignId: data.campaignId as string | undefined,
            });
          }
        }

        const campaignRows: AdminCampaignOption[] = campaignSnap.docs.map((d) => {
          const data = d.data();
          return {
            id: d.id,
            shortCode: data.shortCode || d.id,
            name: data.name || d.id,
            partnerId: data.partnerId || '',
          };
        });

        setPartners(partnerRows);
        setReferrals(referralRows);
        setPurchases(purchaseRows);
        setPayouts(payoutRows);
        setCampaigns(campaignRows);
        setClicks(
          clickSnap.docs.map((d) => {
            const data = d.data();
            return {
              partnerId: data.partnerId as string | undefined,
              platform: data.platform as string | undefined,
              clickedAt: data.clickedAt,
              campaignId: data.campaignId as string | undefined,
              country: data.country as string | undefined,
            };
          }),
        );
      } catch (e) {
        console.error('Admin analytics load failed:', e);
        setLoadError(e instanceof Error ? e.message : 'Failed to load analytics');
      } finally {
        setLoading(false);
      }
  }, []);

  useEffect(() => {
    if (authLoading || !user || !isAdmin) return;
    void loadData();
  }, [user, isAdmin, authLoading, loadData]);

  const handleMarkPaidConfirm = useCallback(async () => {
    if (!markPaidPartner) return;
    setMarkPaidLoading(true);
    setMarkPaidError(null);
    try {
      await markPartnerPayoutsPaid(markPaidPartner.id);
      setMarkPaidPartner(null);
      setLoading(true);
      await loadData();
    } catch (e) {
      if (e instanceof FirebaseError) {
        setMarkPaidError(e.message);
      } else {
        setMarkPaidError(e instanceof Error ? e.message : 'Failed to mark as paid');
      }
    } finally {
      setMarkPaidLoading(false);
    }
  }, [markPaidPartner, loadData]);

  const countries = useMemo(() => {
    const codes = new Set<string>();
    for (const r of referrals) {
      if (r.country) codes.add(r.country);
    }
    for (const c of clicks) {
      if (c.country) codes.add(c.country);
    }
    return [...codes].sort();
  }, [referrals, clicks]);

  const analytics = useMemo(
    () =>
      computeAdminStats(partners, clicks, referrals, purchases, payouts, filters),
    [partners, clicks, referrals, purchases, payouts, filters],
  );

  const handleExportCsv = useCallback(() => {
    const csv = exportAdminTableCsv(analytics.partnerTable);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `partner-analytics-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }, [analytics.partnerTable]);

  if (authLoading) return <div className={styles.loading}>Loading...</div>;

  if (!isAdmin) {
    return (
      <div className={styles.noAccess}>
        <h2>Access Denied</h2>
        <p>This page is only available to administrators.</p>
      </div>
    );
  }

  if (loading) return <div className={styles.loading}>Loading analytics...</div>;

  if (loadError) {
    return (
      <div className={styles.noAccess}>
        <h2>Could not load analytics</h2>
        <p>{loadError}</p>
      </div>
    );
  }

  const {
    totalInstalls,
    periodEarnings,
    totalPaid,
    totalUnpaid,
    totalClicks,
    totalPurchases,
    conversionRate,
    partnerCount,
    pendingPayouts,
    countryBreakdown,
    monthlyInstalls,
    partnerTable,
    hasActiveFilters,
  } = analytics;

  const sortedMonths = Object.entries(monthlyInstalls).sort(([a], [b]) =>
    b.localeCompare(a),
  );
  const sortedCountries = Object.entries(countryBreakdown).sort(([, a], [, b]) => b - a);

  return (
    <div className={styles.page}>
      <h1 className={styles.title}>Admin Analytics</h1>
      <p className={styles.subtitle}>Overview of all affiliate performance.</p>

      <AdminFilters
        filters={filters}
        partners={partners}
        countries={countries}
        campaigns={campaigns}
        onChange={setFilters}
        onExportCsv={handleExportCsv}
      />

      {hasActiveFilters ? (
        <p className={styles.filterHint}>
          Activity stats reflect your selected filters. Paid and unpaid balances are lifetime
          totals.
        </p>
      ) : null}

      <div className={styles.summaryGrid}>
        <div className={styles.summaryCard}>
          <div className={styles.summaryValue}>{totalInstalls}</div>
          <div className={styles.summaryLabel}>Total Installs</div>
        </div>
        <div className={styles.summaryCard}>
          <div className={styles.summaryValue} style={{ color: 'var(--accent)' }}>
            ${periodEarnings.toFixed(2)}
          </div>
          <div className={styles.summaryLabel}>Period Earnings</div>
        </div>
        <div className={styles.summaryCard}>
          <div className={styles.summaryValue}>${totalPaid.toFixed(2)}</div>
          <div className={styles.summaryLabel}>Total Paid</div>
        </div>
        <div className={styles.summaryCard}>
          <div
            className={styles.summaryValue}
            style={{ color: totalUnpaid > 0 ? 'var(--accent)' : 'var(--text-muted)' }}
          >
            ${totalUnpaid.toFixed(2)}
          </div>
          <div className={styles.summaryLabel}>Unpaid Balance</div>
        </div>
      </div>

      <div className={styles.summaryGrid}>
        <div className={styles.summaryCard}>
          <div className={styles.summaryValue}>{totalClicks}</div>
          <div className={styles.summaryLabel}>Total Clicks</div>
        </div>
        <div className={styles.summaryCard}>
          <div className={styles.summaryValue}>{conversionRate}%</div>
          <div className={styles.summaryLabel}>Click-to-Install</div>
        </div>
        <div className={styles.summaryCard}>
          <div className={styles.summaryValue}>{totalPurchases}</div>
          <div className={styles.summaryLabel}>Purchases</div>
        </div>
        <div className={styles.summaryCard}>
          <div className={styles.summaryValue}>{partnerCount}</div>
          <div className={styles.summaryLabel}>Partners</div>
        </div>
      </div>

      <div className={styles.summaryGrid}>
        <div className={styles.summaryCard}>
          <div className={styles.summaryValue}>{pendingPayouts}</div>
          <div className={styles.summaryLabel}>Pending Payouts</div>
        </div>
      </div>

      <div className={styles.section}>
        <h2 className={styles.sectionTitle}>Partner Performance</h2>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Partner</th>
              <th>Code</th>
              <th>Clicks</th>
              <th>Installs</th>
              <th>Purchases</th>
              <th>Earnings</th>
              <th>Paid</th>
              <th>Unpaid</th>
              <th>Eligibility</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {partnerTable.length === 0 ? (
              <tr>
                <td colSpan={11} className={styles.emptyRow}>
                  No partners match the current filters.
                </td>
              </tr>
            ) : (
              partnerTable.map((p) => (
                <tr key={p.id}>
                  <td style={{ fontWeight: 600, color: 'var(--text)' }}>{p.displayName}</td>
                  <td>{p.referralCode}</td>
                  <td>{p.clicks}</td>
                  <td>{p.installs}</td>
                  <td>{p.purchases}</td>
                  <td className={styles.accent}>${p.periodEarnings.toFixed(2)}</td>
                  <td>${p.totalPaid.toFixed(2)}</td>
                  <td
                    style={{
                      color: p.unpaid > 0 ? 'var(--accent)' : 'var(--text-muted)',
                    }}
                  >
                    ${p.unpaid.toFixed(2)}
                  </td>
                  <td>
                    {p.unpaid <= 0 ? (
                      <span className={styles.paidLabel}>—</span>
                    ) : isPayoutEligible(p.unpaid) ? (
                      <span className={styles.eligibleLabel}>Eligible</span>
                    ) : (
                      <span className={styles.belowMinLabel}>Below ${MIN_PAYOUT_USD}</span>
                    )}
                  </td>
                  <td>{p.status}</td>
                  <td>
                    {p.unpaid > 0 && isPayoutEligible(p.unpaid) ? (
                      <button
                        type="button"
                        className={styles.markPaidBtn}
                        onClick={() => {
                          setMarkPaidError(null);
                          setMarkPaidPartner(p);
                        }}
                      >
                        Mark paid
                      </button>
                    ) : (
                      <span className={styles.paidLabel}>—</span>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {markPaidPartner ? (
        <MarkPaidModal
          partner={markPaidPartner}
          payouts={payouts}
          loading={markPaidLoading}
          error={markPaidError}
          onConfirm={() => void handleMarkPaidConfirm()}
          onClose={() => {
            if (!markPaidLoading) setMarkPaidPartner(null);
          }}
        />
      ) : null}

      {sortedCountries.length > 0 && (
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Installs by Country</h2>
          <div className={styles.countryList}>
            {sortedCountries.slice(0, 15).map(([code, count]) => (
              <div key={code} className={styles.countryRow}>
                <span>
                  {countryCodeToFlag(code)} {countryCodeToName(code)}
                </span>
                <span className={styles.countryCount}>
                  {count} {count === 1 ? 'install' : 'installs'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {sortedMonths.length > 0 && (
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Monthly Installs</h2>
          <div className={styles.monthlyGrid}>
            {sortedMonths.map(([month, count]) => (
              <div key={month} className={styles.monthCard}>
                <div className={styles.monthLabel}>{month}</div>
                <div className={styles.monthValue}>{count}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
